import { ReactNode } from 'react';
import { useDrop } from 'react-dnd';

import styled, { css } from '@mint-lib/styled';

import {
  DEFAULT_FIELD_WIDTH,
  MIN_COLUMN_WIDTH,
} from '../../../../../constants/campaignBuilder.js';
import {
  Columns,
  dragItemTypes,
} from '../../../../../constants/myCampaigns.js';
import { Header } from '../../../../../types/shared.js';
import { calcColumnHeaderWidth } from '../../../../../utils/helpers.js';

type DraggableItemType = {
  position: number;
  name: string;
  id: string;
  width?: string;
};

type Props = {
  header: Header;
  onDrop: (dragId: string, dropId: string) => void;
  isDragging: boolean;
  children: ReactNode;
  setRef?: (ref: HTMLDivElement, id: string) => void;
  isResizingColumn?: string;
  scale: number;
};

const HeaderDropZone = ({
  header,
  children,
  onDrop,
  isDragging,
  setRef,
  isResizingColumn,
  scale,
}: Props) => {
  const isExpander = header.id === Columns.EXPANDER;
  const isMask = header.id === Columns.MASK;
  const isActionsField = header.id === Columns.ACTIONS_MENU;
  const isDropDisabled = header.isDragDisabled;
  const isResizing = !!isResizingColumn && header.id === isResizingColumn;

  const headerWidth = calcColumnHeaderWidth({
    isMask,
    isActionsField,
    width: header.width,
    scale,
    minWidth: MIN_COLUMN_WIDTH,
  });

  const [{ isOver }, drop] = useDrop({
    accept: dragItemTypes.HEADER,
    drop: (item: DraggableItemType) => {
      if (isDropDisabled || item.id === header.id) {
        return;
      }

      onDrop(item.id, header.id || '');
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
    }),
  });

  const handleSetRef = (ref: HTMLDivElement) => {
    if (setRef && header.id) {
      setRef(ref, header.id);
    }
    drop(ref);
  };

  return (
    <Th
      ref={handleSetRef}
      isSticky={isActionsField}
      isExpander={isExpander}
      isDragOver={isOver && !isExpander && !isActionsField}
      isDragging={isDragging}
      isDropDisabled={isDropDisabled}
      isMask={isMask}
      isResizing={isResizing}
      width={headerWidth}
      idx={header.idx}
      {...header.getHeaderProps?.(
        !isExpander && header.getSortByToggleProps?.(),
      )}
      className="th"
      data-for={isDropDisabled ? null : header.id}
      data-tip
      scale={scale}
      columnHeader={header.id}
    >
      {children}
    </Th>
  );
};

export const StyledResizeBlock = styled('div')<{ $scale: number }>`
  ${({ $scale, theme }) => css`
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    width: ${10 * $scale}px;
    opacity: 0;
    transition: opacity 0.3s ease;
    border-right: ${2 * $scale}px solid ${theme.palette.ui.tertiary};
  `}
`;

const Th = styled('div')<{
  isSticky: boolean;
  isExpander: boolean;
  isDragOver: boolean;
  isDragging: boolean;
  isDropDisabled: boolean;
  isMask: boolean;
  isResizing: boolean;
  width: number | string;
  idx: number;
  scale: number;
  columnHeader: string;
}>`
  ${({ scale, width }) => css`
    height: ${40 * scale}px;
    width: ${width} !important;
  `}
  position: sticky !important;
  z-index: ${({ idx }) => idx};
  ${({ isExpander, isMask, isSticky }) =>
    !isExpander &&
    !isMask &&
    !isSticky &&
    css`
      cursor: pointer;
    `}

  ${({ isResizing, isExpander, scale, theme }) => css`
    &:hover {
      ${StyledResizeBlock} {
        opacity: 1;
        border-color: ${isResizing
          ? theme.palette.action.focusBorder
          : theme.palette.ui.tertiary};
        border-right-width: ${isExpander ? '4px' : `${2 * scale}px`};
      }
    }
  `}
  ${({ isMask, isSticky, isDragOver, scale, theme }) =>
    !isMask &&
    !isSticky &&
    css`
      &:before {
        position: absolute;
        top: 0;
        left: 0;
        z-index: 1;
        content: '';
        width: ${2 * scale}px;
        height: 100%;
        background-color: ${isDragOver
          ? theme.palette.action.focusBorder
          : 'transparent'};
        pointer-events: none;
      }
    `}
  ${({ isMask, scale, theme }) =>
    isMask &&
    css`
      background: ${theme.palette.ui.primary};
      min-width: ${DEFAULT_FIELD_WIDTH * scale}px !important;
    `}
`;

export default HeaderDropZone;
